import { RouteLocationRaw } from 'vue-router'
import { Modify, StringObject } from './GenericInterfaces'
import { Country, Culture, CultureSpecificity, Language, SimpleValueType } from './Global'
import { ProductInput } from './Import'
import { ProductPropertyStatus, SimplifiedProduct, SimplifiedStaticValue } from './Product'
import { TranslationNamespace } from './Translation'

// Decides which kind of <input type> will be shown during editing
export const PropertyValueTypes = ['string', 'float', 'int', 'date'] as const
export type PropertyValueType = (typeof PropertyValueTypes)[number]

export type PropertySetterValueType = PropertyOptions | string | number
export type SimplePropertySetterValueType = string | number | null // used by PropertyComponent

export type FormattingFunction =
  | 'toPriceFormat'
  | 'toWeightFormat'
  | 'toDimensionsFormat'
  | 'toCommaNumeric'
  | 'toStringFormat'
  | 'getImages'
  | 'toAvailabilityStatusLabel'

export type SuffixFunction = 'getCurrency'

export type PropertyOptions = Array<string | number> | null

export type PropertyReturn = {
  defaultOptions?: PropertyOptions | undefined
  options: PropertyOptions | undefined

  value: string | number | undefined // formatted, joined options
  displayValue: string | number | undefined // formatted, joined options with potential suffix

  delete?: boolean
  filters?: PropertyFilter[]
  status?: ProductPropertyStatus | null
  modified?: {
    value: boolean
    status: boolean
  }
  placeholder?: PropertySetterValueType | undefined
  fallback?: string | number
}

export type PropertyFilter = { displayValue: string | number; link: RouteLocationRaw }

// shared between BaseProperty and PropertySettings (passed to both)
export interface SharedPropertySettings {
  defaultName: string
  hideInInfoSection?: boolean // don't show under additional info on the product page. replaced exclusiveTo
  valueType?: PropertyValueType
  exclusiveTo?: PropertyUsingComponent[]
  multiLine?: boolean
  computedOptions?:
    | 'getAvailableCountries'
    | 'getAvailableLanguages'
    | 'getProductAvailabilityStatuses'
    | 'getAvailableProductTypes'
    | 'getCountryOfOrigin'
    | 'getMadeIn'

  formatting?: FormattingFunction
  formattingExportInstructions?: string // exportservice needs it

  info?: StringObject
  allowMultipleValues?: boolean

  readonly?: boolean
  undeletable?: boolean
  maxLength?: number
  sticky?: boolean

  id?: string // for attributes
}

// used to dynamically add data from taxonomic object
export type AdvancedPropertySettings = SharedPropertySettings & {
  hasTerms?: boolean
  valueType?: PropertyValueType
  exclusiveToCountries?: Country[]
  translateAlphabeticals?: boolean
  doNotTranslateChildren?: boolean // attributes
  /** @deprecated Replaced with fallback */
  standardOptions?: SimplifiedStaticValue[]
}

type PropertyOfType<T extends PropertyType> = T extends 'computed'
  ? {
      type: T
      computeInstructions: string // PropertyComputeInstructionName
      dependencies?: string[]
      showAsFilter?: boolean
    }
  : T extends 'basic' | 'languageSpecific' | 'countrySpecific'
    ? {
        type: T
        customPath: string[]
      }
    : { type: T }

// The way it's stored in the database
export type StoredProperty<T extends PropertyType = PropertyType> = PropertyOfType<T> &
  SharedPropertySettings & {
    translationNamespace?: TranslationNamespace
    headerTranslationNamespace?: TranslationNamespace
    suffix?: SuffixFunction
  }

export type AttributeBaseProperty = StoredProperty<'attribute'>
export type ComputedBaseProperty = StoredProperty<'computed'>
export type BasicBaseProperty = StoredProperty<'basic' | 'languageSpecific' | 'countrySpecific'>

export type AdvancedProperty<T extends PropertyType = PropertyType> = PropertyOfType<T> &
  AdvancedPropertySettings & {
    defaultHeader: string
    header?: string
    name?: string
    translationNamespace?: TranslationNamespace
    headerTranslationNamespace: TranslationNamespace
    culturePrefix?: Country | Language
    // culture?: Partial<Culture>

    key: string
    derivedProperties?: string[] // calculated backwards from dependencies

    get: (product: SimplifiedProduct | undefined, productInput?: ProductInput, explicitTranslationLanguage?: Language) => PropertyReturn //| undefined

    step?: number
    sortFieldPath: string
  }

export type EditableAdvancedProperty<T extends PropertyType = PropertyType> = AdvancedProperty<T> & {
  set: (productInput: ProductInput | undefined, value: PropertySetterValueType, product: SimplifiedProduct | undefined) => boolean
  reset: (productInput: ProductInput | undefined, product: SimplifiedProduct | undefined) => boolean
  setStatus: (productInput: ProductInput | undefined, status: ProductPropertyStatus | null, product: SimplifiedProduct | undefined) => boolean
  resetStatus: (productInput: ProductInput | undefined) => boolean
}

export type PropertyUsingComponent = 'products' | 'filter' | 'export'

export type PropertyType = 'attribute' | 'category' | 'simpleTaxonomy' | CultureSpecificity | 'basic' | 'categories' | 'computed'

export type ValueCallback<T = any> = (..._args: any) => T | undefined

export interface PropertyTemplateInput {
  name: string
  mappings: Array<{ defaultName: string; id?: string }>
}

export interface PropertyTemplate extends PropertyTemplateInput {
  id: string
}

export const EmptyCheckedPlaceholder = 'N/A'
export const EmptyPreliminaryPlaceholder = '?'

// used by util functions in PropertyService
export interface PartialProperty
  extends Modify<
    Partial<AdvancedProperty>,
    {
      defaultName: string
      compute?: string
    }
  > {}

export type PropertyVisibility = 'any-data' | 'value' | 'value-or-preliminary' // any-data == value or status

export interface PropertyDeadlineLevel {
  level: number
  info?: string
  properties: { defaultName: string; id?: string }[]
  colour: string
}

export type PropertyDeadlines = { levels: PropertyDeadlineLevel[] }

export const PropertyDeadlineVisibilities = ['always', 'in edit mode'] as const
export type PropertyDeadlineVisibility = (typeof PropertyDeadlineVisibilities)[number]

export type SelectedPropertyRecord = Partial<Record<number | 'untagged', boolean>>

export type Comparison = { product?: SimplifiedProduct; version?: VersionOption & { productInput: ProductInput } }
// export type Comparison = { product?: SimplifiedProduct; version?: { value: string; productInput: ProductInput } }

export type VersionOption = { value: string | undefined; displayValue: string }

export type PropertyRelation = { defaultName: string; options: string[] }
export type PropertyMappingType = 'supplier' | 'gpsr'
export type PropertyMapping = { dependency: { id: string; defaultName: string }; relations: PropertyRelation[] }

export type InstructionType = 'compute' | 'export'
